import React from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import FinancialPlanning from "./FinancialPlanning";
import MatchScheduler from "./MatchScheduler";
import UserGuide from "./UserGuide";
import TripPlanner from "./TripPlanner";
import TambolaTicketGenerator from "./components/TambolaTicketGenerator";
import Support from "./components/Support";
import PrivacyPolicy from "./components/PrivacyPolicy";
import FitPostPrivacyPolicy from "./components/FitPostPrivacyPolicy";
import { Helmet } from "react-helmet";
import FeedbackFloater from "./components/FeedbackFloater";

function AppRoutes() {
  const location = useLocation();
  // (All unused feedback-related state and handlers removed)
  return (
    <div>
      {location.pathname !== "/financial" && location.pathname !== "/privacy-policy" && location.pathname !== "/fitpost/privacy-policy" && (
        <nav style={styles.nav}>
          <Link to="/" style={styles.link} aria-label="Home">
            <span role="img" aria-label="Home" style={{marginRight: 6}}>🏠</span>Home
          </Link>
          <Link to="/match-scheduler" style={styles.link}>Match Scheduler</Link>
          <Link to="/trip-planner" style={{ ...styles.link, position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            Trip Planner
            <span style={styles.betaBadge}>BETA</span>
          </Link>
          <Link to="/bingo-tickets" style={styles.link}>Bingo Tickets</Link>
          <Link to="/support" style={styles.link}>Support</Link>
        </nav>
      )}
      {location.pathname === "/" && (
        <main style={styles.landingMain}>
          <Helmet>
            <title>Truth AI Tech – Free Sports & Trip Planning Tools</title>
            <meta name="description" content="Free, easy-to-use tools for sports leagues, travel, and events. Match Scheduler, Trip Planner, and Tambola Ticket Generator – all in one modern, mobile-friendly suite. No sign-up required!" />
            <link rel="canonical" href="https://truth-ai-tech.com/" />
          </Helmet>
          <section style={styles.heroSection}>
            <h1 style={styles.heroTitle}>Welcome to Truth AI Tech</h1>
            <p style={styles.heroSubtitle}>Your all-in-one suite for sports scheduling, trip planning, and party fun – powered by AI.</p>
            <img src="/logo.png" alt="Truth AI Tech Logo" style={styles.heroLogo} />
            <div style={styles.ctaWrap}>
              <Link to="/match-scheduler" style={styles.ctaButton}>Try Match Scheduler</Link>
              <Link to="/trip-planner" style={styles.ctaButtonAlt}>Plan a Trip</Link>
              <Link to="/bingo-tickets" style={styles.ctaButtonAlt}>Generate Tambola Tickets</Link>
            </div>
          </section>
          <section style={styles.featuresSection}>
            <div style={styles.featureCard}>
              <h2 style={styles.featureTitle}>⚽ Match Scheduler</h2>
              <p style={styles.featureDesc}>Generate fair, balanced round-robin schedules for your sports league or tournament in seconds. Export to Excel, assign umpires, and more.</p>
              <Link to="/match-scheduler" style={styles.featureButton}>Match Scheduler</Link>
            </div>
            <div style={styles.featureCard}>
              <h2 style={styles.featureTitle}>🌏 Trip Planner <span style={styles.betaBadge}>BETA</span></h2>
              <p style={styles.featureDesc}>Plan your next adventure with AI-powered itineraries, packing lists, and travel tips. Perfect for families, friends, and solo travelers.</p>
              <Link to="/trip-planner" style={styles.featureButton}>Plan a Trip</Link>
            </div>
            <div style={styles.featureCard}>
              <h2 style={styles.featureTitle}>🎲 Tambola / Housie Ticket Generator</h2>
              <p style={styles.featureDesc}>Create professional, printable Tambola (Bingo) tickets for your next party or event. PDF download, cut-friendly, and rules-compliant.</p>
              <Link to="/bingo-tickets" style={styles.featureButton}>Get Tambola Tickets</Link>
            </div>
          </section>
          <section style={styles.aboutSection}>
            <h2 style={styles.aboutTitle}>Why Truth AI Tech?</h2>
            <ul style={styles.aboutList}>
              <li>✅ 100% Free, no sign-up required</li>
              <li>✅ Modern, mobile-friendly design</li>
              <li>✅ AI-powered for smarter planning</li>
              <li>✅ Trusted by sports clubs, families, and event organizers</li>
            </ul>
          </section>
          <footer style={styles.footerSection}>
            <div style={styles.footerLinks}>
              <Link to="/privacy-policy" style={styles.footerLink}>Privacy Policy</Link>
              <span style={styles.footerDivider}>•</span>
              <Link to="/support" style={styles.footerLink}>Support</Link>
            </div>
            <p style={styles.footerCopyright}>© 2026 Truth AI Tech. All rights reserved.</p>
          </footer>
        </main>
      )}
      <Routes>
        <Route path="/financial" element={<FinancialPlanning />} />
        <Route path="/trip-planner" element={<TripPlanner />} />
        <Route path="/match-scheduler" element={<MatchScheduler />} />
        <Route path="/user-guide" element={<UserGuide />} />
        <Route path="/bingo-tickets" element={<TambolaTicketGenerator />} />
        <Route path="/support" element={<Support />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/fitpost/privacy-policy" element={<FitPostPrivacyPolicy />} />
      </Routes>
      {/* Global Feedback Floater, except on /match-scheduler, /user-guide, /financial, and /support */}
      {!["/match-scheduler", "/user-guide", "/financial", "/support", "/privacy-policy", "/fitpost/privacy-policy"].includes(location.pathname) && (
        <FeedbackFloater hide={false} />
      )}
    </div>
  );
}

const styles = {
  nav: {
    backgroundColor: "#007bff",
    padding: "10px",
    display: "flex",
    justifyContent: "center",
    gap: "20px",
  },
  link: {
    color: "white",
    textDecoration: "none",
    fontSize: "18px",
    fontWeight: "bold",
  },
  landingMain: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '40px 16px',
    background: 'linear-gradient(135deg, #f3f6fa 0%, #e3f0ff 100%)',
    borderRadius: 24,
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  },
  heroSection: {
    textAlign: 'center',
    marginBottom: 48,
    padding: '32px 0 16px 0',
  },
  heroTitle: {
    fontSize: 44,
    fontWeight: 900,
    color: '#007bff',
    marginBottom: 10,
    letterSpacing: '-1px',
  },
  heroSubtitle: {
    fontSize: 22,
    color: '#333',
    marginBottom: 18,
    fontWeight: 500,
  },
  heroLogo: {
    width: 90,
    height: 90,
    margin: '0 auto 18px auto',
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
    background: '#fff',
    display: 'block',
  },
  ctaWrap: {
    display: 'flex',
    justifyContent: 'center',
    gap: 18,
    marginTop: 18,
    flexWrap: 'wrap',
  },
  ctaButton: {
    background: 'linear-gradient(90deg, #007bff 0%, #00c6ff 100%)',
    color: 'white',
    padding: '14px 32px',
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 18,
    textDecoration: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    transition: 'background 0.2s',
    marginBottom: 8,
  },
  ctaButtonAlt: {
    background: '#fff',
    color: '#007bff',
    padding: '14px 32px',
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 18,
    textDecoration: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    border: '2px solid #007bff',
    marginBottom: 8,
    transition: 'background 0.2s, color 0.2s',
  },
  featuresSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 32,
    justifyContent: 'center',
    marginBottom: 48,
  },
  featureCard: {
    background: '#fff',
    borderRadius: 18,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: 36,
    minWidth: 260,
    maxWidth: 320,
    flex: '1 1 260px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: '1.5px solid #e3f0ff',
  },
  featureTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#007bff',
    marginBottom: 10,
    letterSpacing: '-0.5px',
  },
  featureDesc: {
    fontSize: 16,
    color: '#444',
    marginBottom: 18,
    fontWeight: 400,
  },
  featureButton: {
    background: '#007bff',
    color: 'white',
    padding: '10px 20px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 16,
    marginTop: 'auto',
    transition: 'background 0.2s',
    display: 'inline-block',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
  },
  betaBadge: {
    background: '#ff9800',
    color: 'white',
    fontSize: '10px',
    fontWeight: 'bold',
    borderRadius: '4px',
    padding: '2px 6px',
    marginLeft: '6px',
    letterSpacing: '1px',
    lineHeight: 1,
    verticalAlign: 'middle',
    position: 'relative',
    top: '-1px',
  },
  aboutSection: {
    background: '#f8fafc',
    borderRadius: 18,
    boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
    padding: 32,
    margin: '0 auto',
    maxWidth: 700,
    textAlign: 'center',
  },
  aboutTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#007bff',
    marginBottom: 12,
  },
  aboutList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    fontSize: 16,
    color: '#333',
    fontWeight: 500,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignItems: 'center',
  },
  footerSection: {
    marginTop: 48,
    paddingTop: 32,
    borderTop: '1px solid #e3e8f0',
    textAlign: 'center',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  footerLink: {
    color: '#007bff',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    transition: 'color 0.2s',
  },
  footerDivider: {
    color: '#cbd5e1',
    fontSize: 14,
  },
  footerCopyright: {
    fontSize: 13,
    color: '#64748b',
    margin: 0,
  },
};

export default AppRoutes;
